import { Injectable } from '@angular/core';

const STORAGE_KEY = 'chatbot-widget-position';
const DRAG_THRESHOLD = 5;
const INIT_FLAG = 'chatDragInit';

@Injectable({ providedIn: 'root' })
export class ChatBubbleDragService {
  // The button lives inside the shadow DOM of <chat-widget>.
  // getElementById / contains() do not cross shadow boundaries —
  // we must use shadowRoot.querySelector and composedPath().
  private getBtn(): HTMLElement | null {
    const host = document.querySelector('chat-widget');

    return (
      (host?.shadowRoot?.querySelector(
        '#chatbot-widget-window-button',
      ) as HTMLElement | null) ?? null
    );
  }

  init(): void {
    this.applyToBtn();

    if (document.body.dataset[INIT_FLAG] === 'true') {
      return;
    }

    const btn = this.getBtn();

    if (!btn) {
      return;
    }

    document.body.dataset[INIT_FLAG] = 'true';
    this.registerListeners();
  }

  private applyToBtn(): void {
    const btn = this.getBtn();

    if (!btn) {
      return;
    }

    // Re-apply protective styles on every call — the widget may re-render
    // and clear them, which would let the browser take over the gesture.
    btn.style.cursor = 'grab';
    btn.style.touchAction = 'none';

    (
      btn.style as CSSStyleDeclaration & { webkitTouchCallout: string }
    ).webkitTouchCallout = 'none';

    btn.draggable = false;

    btn.querySelectorAll<HTMLElement>('*').forEach((el) => {
      el.draggable = false;
      el.style.touchAction = 'none';
    });

    // Position restore runs only once per button instance.
    if (btn.dataset['draggable'] === 'true') {
      return;
    }

    btn.dataset['draggable'] = 'true';

    const saved = sessionStorage.getItem(STORAGE_KEY);

    if (!saved) {
      return;
    }

    try {
      const { left, top } = JSON.parse(saved) as {
        left: string;
        top: string;
      };

      btn.style.left = left;
      btn.style.right = 'auto';
      btn.style.top = top;
      btn.style.bottom = 'auto';
    } catch {
      // ignore invalid saved data
    }
  }

  private registerListeners(): void {
    let startX = 0,
      startY = 0,
      startLeft = 0,
      startTop = 0;

    let dragging = false;
    let moved = false;

    document.addEventListener(
      'dragstart',
      (e) => {
        const b = this.getBtn();

        if (b && e.composedPath().includes(b)) {
          e.preventDefault();
        }
      },
      true,
    );

    document.addEventListener(
      'pointerdown',
      (e) => {
        const b = this.getBtn();

        if (!b || !e.composedPath().includes(b)) {
          return;
        }

        const rect = b.getBoundingClientRect();
        startX = e.clientX;
        startY = e.clientY;
        startLeft = rect.left;
        startTop = rect.top;
        dragging = true;
        moved = false;
        b.style.cursor = 'grabbing';
        e.preventDefault();
      },
      true,
    );

    document.addEventListener(
      'pointermove',
      (e) => {
        if (!dragging) {
          return;
        }

        const b = this.getBtn();

        if (!b) {
          dragging = false;

          return;
        }

        const dx = e.clientX - startX;
        const dy = e.clientY - startY;

        if (
          !moved &&
          Math.abs(dx) < DRAG_THRESHOLD &&
          Math.abs(dy) < DRAG_THRESHOLD
        ) {
          return;
        }

        moved = true;
        b.style.left = `${Math.max(0, Math.min(window.innerWidth - b.offsetWidth, startLeft + dx))}px`;
        b.style.right = 'auto';
        b.style.top = `${Math.max(0, Math.min(window.innerHeight - b.offsetHeight, startTop + dy))}px`;
        b.style.bottom = 'auto';
        e.preventDefault();
      },
      { capture: true, passive: false },
    );

    const endDrag = () => {
      if (!dragging) {
        return;
      }

      dragging = false;
      const b = this.getBtn();

      if (!b) {
        return;
      }

      b.style.cursor = 'grab';

      if (moved) {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ left: b.style.left, top: b.style.top }),
        );
      }
    };

    document.addEventListener('pointerup', endDrag, true);
    // pointercancel fires on mobile when the browser takes over the gesture
    // (e.g. scroll, long-press menu). Treat it the same as pointerup so the
    // drag state is cleaned up and the position is saved.
    document.addEventListener('pointercancel', endDrag, true);

    let resizeTimer: ReturnType<typeof setTimeout> | undefined;

    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);

      resizeTimer = setTimeout(() => {
        const b = this.getBtn();

        if (!b || !b.style.left) {
          return;
        }

        localStorage.removeItem(STORAGE_KEY);
        b.style.left = '30px';
        b.style.right = 'auto';
        b.style.top = 'auto';
        b.style.bottom = '30px';
        b.dataset['draggable'] = '';
      }, 50);
    });

    document.addEventListener(
      'click',
      (e) => {
        const b = this.getBtn();

        if (moved && b && e.composedPath().includes(b)) {
          e.stopPropagation();
          e.preventDefault();
          moved = false;
        }
      },
      true,
    );
  }
}
